import { createDocValueStore } from './docStore';
import { getBrandInfo } from './brandStore';
import {
  NO_INFO_ERROR, NO_PAGES_ERROR, suggestListings, type ListingCall, type ListingCandidate, type SuggestPage,
} from './listingSuggest';

// ONE "FIND THE LINKS" SEARCH PER DOCUMENT, held apart from both places that show it: the
// badge that runs it sits with Phase 1's other reads, and what it proposes sits over the
// link rows it fills (see ListingSuggestions). Kept out of doc-state on purpose, as it
// always was: nothing is saved by searching — a proposal only counts once somebody
// presses + on it.
export type ListingSearch = {
  busy: boolean;
  // Which failure, not its words: the words are the reader's language, at render.
  error: '' | 'noPages' | 'noInfo' | 'failed';
  // null until a search has run, so "nothing found" and "not asked yet" are different
  // states — the first has a line to show and the second has nothing to say.
  found: ListingCandidate[] | null;
  // The calls that search made, kept even when it found nothing: "why did it come back
  // empty" is answered by the prompt and the reply, and that is the moment somebody
  // wants to read them most.
  calls: ListingCall[];
  // And the pages it sent them, which only this side holds (see ListingSearch).
  pages: SuggestPage[];
  searchError: string;
  // WHICH SEARCH THESE CALLS BELONG TO, counted up on every press. It is part of the
  // badges' keys, so a second search builds new ones: a badge holds the prompt it first
  // loaded and only re-asks when its `revision` changes (see usePromptCall), and these
  // do not fetch — their prompt is a closure over the call they were made with. Reusing
  // the instance would show the previous search's question under the new one's results.
  epoch: number;
};

const store = createDocValueStore<ListingSearch>({
  busy: false, error: '', found: null, calls: [], pages: [], searchError: '', epoch: 0,
});

export const useListingSearch = store.useValue;

export async function searchListings(docId: string, file: Blob) {
  store.set(docId, { ...store.get(docId), busy: true, error: '' });
  try {
    const result = await suggestListings(file, getBrandInfo(docId));
    store.set(docId, {
      busy: false, error: '', found: result.listings, calls: result.calls, pages: result.pages,
      searchError: result.searchError,
      epoch: store.get(docId).epoch + 1,
    });
  } catch (e) {
    store.set(docId, {
      ...store.get(docId), busy: false, found: null, calls: [], pages: [], searchError: '',
      error: !(e instanceof Error) ? 'failed'
        : e.message === NO_PAGES_ERROR ? 'noPages'
          : e.message === NO_INFO_ERROR ? 'noInfo' : 'failed',
    });
  }
}
